import {useMemo, useState} from "react";
import {DialogBody, DialogFooter, HTMLSelect, HTMLTable, Intent, Tag, Text} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import uniq from "lodash/uniq";
import {AgStop} from "../../common/types";
import {RowData} from "../types/types";
import {getStopDateTimeCellValue, StopDateTimeType} from "../../common/cell-renderers/DateTimeCellRenderer";
import {StackedFieldCellRenderer} from "../../common/cell-renderers/DoubleFieldCellRenderer";
import {ManifestJobColorPalette} from "../../common/cell-renderers/ColoredJobNumberCellRenderer";
import {ServiceBadge} from "../../common/ServiceBadge";
import {renderStopTypeIcon} from "../../common/icons/StopTypeIcons";
import {useAppContext} from "../../../ApplicationContext";
import {usePreferenceContext, extractSimplePref} from "../../../providers/PreferenceProvider";
import {TenantPreferences, EmptyValueStrings} from "../../../constants/constants";
import {CancelButton, StyledButton} from "../../common/StyledButtons";
import {DriverLabel, StyledDialog, TransferRow} from "../styles/styleds";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  stop: AgStop | null;
  sourceDriverId: number;
  sourceRouteId: string;
  workingRowData: RowData[];
  /** False (or a promise of false) keeps the dialog open: the DP-3230 skill check stopped the move. */
  onMoveJob: (jobId: number, sourceRouteId: string, targetRouteId: string) => boolean | Promise<boolean>;
};

const MoveJobDialog = ({isOpen, onClose, stop, sourceDriverId, sourceRouteId, workingRowData, onMoveJob}: Props) => {
  const [targetRouteId, setTargetRouteId] = useState<string | null>(null);
  const {appState} = useAppContext();
  const {tenantPreferences} = usePreferenceContext();
  const isTimezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false)
    .value as boolean;

  const otherRoutes = workingRowData.filter((row) => row.routeId !== sourceRouteId);
  const sourceDriver = workingRowData.find((row) => row.routeId === sourceRouteId);
  const jobId = stop?.job?.jobId;

  // DP-3111: the Move Job action moves the ENTIRE job, so the dialog must list every stop of the
  // job that will move together — not just the clicked stop. Preserves the source driver's
  // assignedStops order (already in manifest sequence).
  const jobStops = useMemo(() => {
    if (!stop) return [];
    // Guard on a concrete jobId so a job-less clicked stop doesn't group all other job-less stops.
    const matching = (sourceDriver?.assignedStops ?? []).filter((s) => jobId != null && s.job?.jobId === jobId);
    // Fall back to the clicked stop if the source route can't be resolved, so the table is never empty.
    return matching.length > 0 ? matching : [stop];
  }, [sourceDriver?.assignedStops, stop, jobId]);

  const jobBackgroundColor = useMemo(() => {
    if (!stop) return undefined;
    const stops = sourceDriver?.assignedStops ?? [];
    const jobNumbers = uniq(stops.map((s) => s.job?.jobNumber).filter(Boolean)) as string[];
    if (jobNumbers.length > ManifestJobColorPalette.length) return undefined;
    const index = jobNumbers.indexOf(stop.job?.jobNumber ?? "");
    return index >= 0 ? ManifestJobColorPalette[index] : undefined;
  }, [sourceDriver?.assignedStops, stop]);

  if (!stop) return null;

  const finishMove = (moved: boolean) => {
    // Stay open with the chosen route so the dispatcher can pick another one.
    if (moved === false) return;
    onClose();
    setTargetRouteId(null);
  };

  const handleMove = () => {
    if (!jobId || !targetRouteId) return;
    const moved = onMoveJob(jobId, sourceRouteId, targetRouteId);
    if (moved instanceof Promise) {
      void moved.then(finishMove);
    } else {
      finishMove(moved);
    }
  };

  const handleClose = () => {
    onClose();
    setTargetRouteId(null);
  };

  return (
    <StyledDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Move Job"
      style={{maxWidth: "80%", width: "fit-content"}}
    >
      <DialogBody>
        <TableScrollContainer>
          <StyledTable>
            <thead>
              <tr>
                <th>JOB</th>
                <th>TYPE</th>
                <th>TIME</th>
                <th>ETA</th>
                <th>ADDRESS</th>
                <th>SVT</th>
                <th>PC. | WT.</th>
                <th>STOP NAME</th>
              </tr>
            </thead>
            <tbody>
              {jobStops.map((jobStop) => {
                const scheduledTimeValue = getStopDateTimeCellValue({
                  data: jobStop,
                  is24HourFormat: appState.isTimeFormat24hr,
                  isTimezoneOfJobStop,
                  typeOfDateTime: StopDateTimeType.SCHEDULED_TIME
                });
                const etaValue = getStopDateTimeCellValue({
                  data: jobStop,
                  is24HourFormat: appState.isTimeFormat24hr,
                  isTimezoneOfJobStop,
                  showPreposition: false,
                  typeOfDateTime: StopDateTimeType.ETA
                });
                return (
                  <tr key={jobStop.jobStopId} data-testid="move-job-stop-row">
                    <td>
                      <ManifestJobBadge intent={Intent.PRIMARY} minimal style={jobBackgroundColor}>
                        <LinkText ellipsize>{jobStop.job?.jobNumber}</LinkText>
                      </ManifestJobBadge>
                    </td>
                    <td>
                      <StopTypeIconDiv>{renderStopTypeIcon(jobStop.stopType ?? "")}</StopTypeIconDiv>
                    </td>
                    <td>{scheduledTimeValue && StackedFieldCellRenderer(scheduledTimeValue)}</td>
                    <td>{etaValue && StackedFieldCellRenderer(etaValue)}</td>
                    <td>
                      {StackedFieldCellRenderer({
                        upperField: {value: jobStop.address ?? "", tooltip: jobStop.address ?? ""},
                        lowerField: {
                          value: [jobStop.city, jobStop.state, jobStop.zip].filter(Boolean).join(", "),
                          style: {color: "#6B7280"}
                        }
                      })}
                    </td>
                    <td>
                      <ServiceBadge service={jobStop.job?.service ?? undefined} ellipsize />
                    </td>
                    <td>
                      {jobStop.job?.pieces ?? EmptyValueStrings.notAvailable} |{" "}
                      {jobStop.job?.weight ?? EmptyValueStrings.notAvailable}
                    </td>
                    <td>{jobStop.name ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </TableScrollContainer>
        <TransferRow style={{marginTop: "24px"}}>
          <DriverLabel>{sourceDriver?.name ?? sourceDriver?.driverCode ?? "Unknown"}</DriverLabel>
          <FontAwesomeIcon icon={solid("arrow-right")} color="#6B7280" />
          <HTMLSelect
            value={targetRouteId ?? ""}
            onChange={(e) => setTargetRouteId(e.target.value || null)}
            style={{borderRadius: "6px"}}
            iconName={"caret-down"}
          >
            <option value="">Select route...</option>
            {otherRoutes.map((route) => {
              const routeIndex = workingRowData.indexOf(route) + 1;
              const driverLabel = route.name ?? route.driverCode ?? `Driver ${route.driverId}`;
              return (
                <option key={route.routeId} value={route.routeId}>
                  Route {routeIndex} - {driverLabel}
                </option>
              );
            })}
          </HTMLSelect>
        </TransferRow>
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <CancelButton onClick={handleClose}>Cancel</CancelButton>
            <StyledButton intent="primary" onClick={handleMove} disabled={!targetRouteId}>
              Move
            </StyledButton>
          </>
        }
      />
    </StyledDialog>
  );
};

export default MoveJobDialog;

// DP-3111: a job can have many stops; bound the height and scroll so the dialog never overflows the
// screen. The scroll + border/radius live here (not on the table) so the table is NOT its own scroll
// container — otherwise the sticky <thead> below would resolve against the table and never pin.
const TableScrollContainer = styled.div`
  max-height: 40vh;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const StyledTable = styled(HTMLTable)`
  thead {
    background-color: #f3f4f6;
    font-size: 12px;
    position: sticky;
    top: 0;
    z-index: 1;

    th {
      color: #4b5563;
      font-weight: normal;
      letter-spacing: 0;
    }
  }

  tbody td {
    vertical-align: middle;
  }

  /* Separate each stop row so multiple stops of a job are visually distinct */
  tbody tr:not(:last-of-type) td {
    border-bottom: 1px solid #e5e7eb;
  }

  tbody tr:nth-of-type(even) {
    background-color: #f9fafb;
  }
`;

const ManifestJobBadge = styled(Tag)`
  border-radius: 4px;
  background-color: transparent;
`;

const LinkText = styled(Text)`
  opacity: 1;
  font-family: Roboto;
  font-weight: 500;
  font-size: 12px;
  color: #1f2937;
`;

const StopTypeIconDiv = styled.div`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
`;
